import * as React from "react";
import { useParams, useHistory } from "react-router-dom";
import DetailInfo from "../../../components/Details/OverviewInfo/DetailInfo/DetailInfo";
import {
  OverviewTitle,
  OverviewTitleWrapper,
} from "../../../components/Details/OverviewInfo/styles";
import { EmailTag, PhoneTag } from "../../../components/Displays";
import { PrimaryButton } from "../../../components/Forms/Base/Buttons";
import OverlaySpinner from "../../../components/Loaders/OverlaySpinner";
import {
  BodyText,
  DetailInnerWrapper,
  DetailPageBox,
  DetailPageBoxFlexWrapper,
} from "../../../components/sharedStyles";
import { buildQueryString, setActiveFormInstance } from "../../../store/base";
import { useDebtorInvoicing } from "../../../store/invoicingDebtor";
import DebtorInvoiceForm from "../../../components/Forms/InvoicingDebtor/ChapterForm/ModalForm";
import { useTenant } from "../../../store/tenants";
import { useDispatch } from "react-redux";
import { constants as invoicingDebtorConstants } from "../../../store/invoicingDebtor";
import InvoicingErrorTable from "../../../components/Tables/InvoicingErrors/FullTable";
import { useInvoicingErrorPaginationCount } from "../../../store/invoicingErrors/hooks/retrieve";
import useQuery from "../../../components/utils/useQuery";
import DebtorAccountBalance from "../../Tenant/detail/AccountBalance";
import ContractViewInvoicesTable from "../../../components/Billecta/Invoices/ContractViewInvoicesTable";
import { useInvoicesFromMultipleSearchResults } from "../../../store/billectaSearch";
import { addToast, TOAST_TYPES } from "../../../store/toasts";
import { invoiceDetailUrl } from "../../../store/billectaInvoicing";

export default function MemberInvoicing() {
  const dispatch = useDispatch();
  const [editOpen, setEditOpen] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const { tenantId } = useParams();

  const { push } = useHistory();
  const query = useQuery();
  const openSettings = query.get("opensettings") == "true";

  const [member] = useTenant(tenantId);

  const [debtorInvoiceConfig, debtorInvoiceConfigLoading] = useDebtorInvoicing(
    member?.debtor_invoice_config?.id
  );

  const [errorCount] = useInvoicingErrorPaginationCount({
    filters: {
      debtorinvoiceconfig: member?.debtor_invoice_config?.id || "",
    },
  });

  const errorQuery = {
    debtorinvoiceconfig: member?.debtor_invoice_config?.id,
  };

  const tenantBillectaIds = debtorInvoiceConfig?.billecta_ids?.map((bid) => ({
    creditorId: bid.creditor_id,
    debtorId: bid.object_id,
  }));

  const searchInstances = tenantBillectaIds?.map((tbi) => {
    const params = {
      CreditorPublicId: tbi.creditorId,
      DebtorPublicIds: [tbi.debtorId],
    };

    return params;
  });

  const [invoices, invoicesLoading] = useInvoicesFromMultipleSearchResults({
    searchInstances,
    searchId: member?.debtor_invoice_config?.id,
    typeConstants: invoicingDebtorConstants,
    errorCallback: () => {
      dispatch(
        addToast({
          type: TOAST_TYPES.ERROR,
          title: "Kunde inte hämta avier för medlem",
          description: "Kontakta Pigello om problemet kvarstår",
        })
      );
    },
  });

  const openCreateDebtorInvoiceForm = () => {
    const initialData = {
      tenant: { id: tenantId },
      email_invoice: member?.user?.email,
      your_reference: member?.user?.corporate_name
        ? member.user.corporate_name
        : `${member?.user?.first_name || ""} ${member?.user?.last_name || ""}`,
    };

    dispatch(
      setActiveFormInstance({
        storeName: invoicingDebtorConstants.STORE_NAME,
        data: initialData,
      })
    );

    setCreateOpen(true);
  };

  React.useEffect(() => {
    if (openSettings) {
      openCreateDebtorInvoiceForm();
    }
  }, [openSettings]);

  const handleInvoiceRowClicked = (row) => {
    const { original } = row;
    push(
      invoiceDetailUrl({
        invoiceId: original.ActionPublicId,
        creditorId: original.CreditorPublicId,
      })
    );
  };

  return (
    <>
      <DebtorInvoiceForm
        method="PATCH"
        id={debtorInvoiceConfig?.id}
        isOpen={editOpen}
        onCheckout={() => setEditOpen(false)}
        instance={debtorInvoiceConfig}
        kind={member?.user?.corporate_name ? "company" : "private"}
        isMember
      />

      <DebtorInvoiceForm
        method="POST"
        isOpen={createOpen}
        onCheckout={() => setCreateOpen(false)}
        kind={member?.user?.corporate_name ? "company" : "private"}
        isMember
      />

      <DetailInnerWrapper>
        {errorCount > 0 && (
          <DetailPageBox error>
            <OverviewTitleWrapper>
              <OverviewTitle small>Felmeddelanden</OverviewTitle>
            </OverviewTitleWrapper>

            <InvoicingErrorTable
              isBare
              ignoreLocalStorage
              persistantQuery={errorQuery}
            />
          </DetailPageBox>
        )}

        <DetailPageBoxFlexWrapper>
          <DetailPageBox
            style={{ flex: 3, alignSelf: "flex-start", maxWidth: "70%" }}
          >
            {debtorInvoiceConfigLoading && <OverlaySpinner />}
            <OverviewTitleWrapper>
              <OverviewTitle>Aviseringsinställning</OverviewTitle>

              {debtorInvoiceConfig && (
                <PrimaryButton
                  title="Redigera"
                  clicked={() => setEditOpen(true)}
                />
              )}
            </OverviewTitleWrapper>

            {!debtorInvoiceConfig ? (
              <div
                style={{
                  width: "100%",
                  height: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PrimaryButton
                  title="Sätt upp aviseringsinställning"
                  clicked={openCreateDebtorInvoiceForm}
                />
              </div>
            ) : (
              <>
                <DetailInfo
                  infoObj={getInfoObj({ debtor: debtorInvoiceConfig, member })}
                />
              </>
            )}
          </DetailPageBox>

          <div
            style={{
              flex: 1,
              minWidth: "25%",
              alignSelf: "flex-start",
              flexDirection: "column",
              display: "flex",
            }}
          >
            {debtorInvoiceConfig?.billecta_ids?.map((billectaObj) => (
              <DebtorAccountBalance {...{ billectaObj, tenant: member }} />
            ))}
          </div>
        </DetailPageBoxFlexWrapper>

        {tenantBillectaIds?.length > 0 && (
          <DetailPageBox>
            <OverviewTitleWrapper>
              <OverviewTitle small>Avier till medlem</OverviewTitle>
            </OverviewTitleWrapper>

            {invoicesLoading && <OverlaySpinner />}

            {invoices?.length ? (
              <ContractViewInvoicesTable
                invoices={invoices}
                handleRowClicked={handleInvoiceRowClicked}
              />
            ) : (
              <BodyText>
                {invoicesLoading
                  ? "Laddar avier..."
                  : "Inga avier har genererats än för denna medlem"}
              </BodyText>
            )}
          </DetailPageBox>
        )}
      </DetailInnerWrapper>
    </>
  );
}

function getInfoObj({ debtor, member }) {
  const infoObj = {
    Faktureringsinfo: [
      {
        title: "Fakturaadress",
        value: debtor.invoice_address?.base?.split(",")?.[0] || "-",
      },
      {
        title: "Fakturaadress postnr.",
        value: debtor.invoice_address?.postal_code || "-",
      },
      {
        title: "Fakturaadress stad",
        value: debtor.invoice_address?.city || "-",
      },
      {
        title: "Fakturaadress land",
        value: debtor.invoice_address?.country || "-",
      },
      {
        title: "c/o",
        value: debtor.care_of || "-",
      },
    ],
    "Leverans av faktura": [
      {
        title: "Leveransmetod",
        value: debtor.delivery_method_display || "Ej specificerad",
      },
      {
        title: "Kundens referens",
        value: debtor.your_reference || "-",
      },
      { title: "E-post", value: <EmailTag mail={debtor?.email_invoice} /> },
      {
        title: "E-post CC",
        value: <EmailTag mail={debtor?.email_invoice_cc} />,
      },
      {
        title: "E-post BCC",
        value: <EmailTag mail={debtor?.email_invoice_bcc} />,
      },
      { title: "Telefon", value: <PhoneTag phone={debtor?.invoice_phone} /> },
    ],

    Betalmetoder: [
      {
        title: "Autogiro betalarnummer",
        value: debtor.autogiro_payer_number || "Ej uppsatt",
      },
      {
        title: "Autogiro clearing",
        value: debtor.autogiro_clearing || "Ej uppsatt",
      },
      {
        title: "Autogironummer",
        value: debtor.autogiro_number || "Ej uppsatt",
      },
      {
        title: "Autogiroleverantör",
        value: debtor.autogiro_payment_service || "Ej uppsatt",
      },
    ],
  };

  const tenantIsCompany = member?.corporate_name ? true : false;

  if (tenantIsCompany) {
    infoObj["Faktureringsinfo"].push({
      title: "VAT-nummer",
      value: debtor.vat || "-",
    });

    infoObj["Betalmetoder"].push({
      title: "Intermediator",
      value: debtor.intermediator_display || "Ej uppsatt",
    });

    infoObj["Betalmetoder"].push({
      title: "GLN",
      value: debtor.gln || "Ej uppsatt",
    });
  } else {
    infoObj["Betalmetoder"].push({
      title: "E-faktura bank",
      value: debtor.e_invoice_bank_display || "Ej uppsatt",
    });
  }

  return infoObj;
}
